import {
  Block,
  CheckCircleOutline,
  HighlightOff,
  RadioButtonUnchecked,
  Tonality,
} from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';

export type SessionAttendanceInput = {
  billable: boolean | null;
  attended: boolean | null;
  noShow: boolean | null;
  partialAttendanceMinutes: number | null;
};

export type SessionAttendanceStatusValue =
  | 'attended'
  | 'partial'
  | 'no-show'
  | 'late-cancel'
  | 'no-show-or-late-cancel'
  | 'rescheduled';

export function getAttendanceStatus(
  attendee: SessionAttendanceInput | null | undefined,
): SessionAttendanceStatusValue | null {
  if (!attendee) return null;
  const { billable, attended, noShow, partialAttendanceMinutes } = attendee;

  if (billable == null && attended == null) return null;
  if (partialAttendanceMinutes && billable && attended) return 'partial';
  if (billable && attended) return 'attended';
  if (billable) {
    if (noShow === true) return 'no-show';
    if (noShow === false) return 'late-cancel';
    return 'no-show-or-late-cancel';
  }
  return 'rescheduled';
}

type StatusDisplay = { label: string; color: string; muted?: boolean; Icon: typeof Block };

const STATUS_CONFIG: Record<SessionAttendanceStatusValue, StatusDisplay> = {
  attended: { label: 'Attended', color: 'success.main', Icon: CheckCircleOutline },
  partial: { label: 'Partial', color: 'warning.main', Icon: Tonality },
  'no-show': { label: 'No-show', color: 'error.main', Icon: HighlightOff },
  'late-cancel': { label: 'Late cancellation', color: 'error.main', Icon: HighlightOff },
  'no-show-or-late-cancel': {
    label: 'No-show / Late cancellation',
    color: 'error.main',
    Icon: HighlightOff,
  },
  rescheduled: { label: 'Cancelled with notice', color: 'text.primary', Icon: Block },
};

const NOT_MARKED: StatusDisplay = {
  label: 'Not marked',
  color: 'text.disabled',
  muted: true,
  Icon: RadioButtonUnchecked,
};

export function getAttendanceLabel(attendee: SessionAttendanceInput | null | undefined): string {
  const status = getAttendanceStatus(attendee);
  return status ? STATUS_CONFIG[status].label : NOT_MARKED.label;
}

type SessionAttendanceStatusProps = {
  attendee: SessionAttendanceInput | null | undefined;
  iconOnly?: boolean;
};

export function SessionAttendanceStatus({ attendee, iconOnly }: SessionAttendanceStatusProps) {
  const status = getAttendanceStatus(attendee);
  const { label, color, muted, Icon } = status ? STATUS_CONFIG[status] : NOT_MARKED;

  if (iconOnly) {
    return (
      <Tooltip title={label}>
        <Icon fontSize="small" sx={{ color }} />
      </Tooltip>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Icon fontSize="small" sx={{ color }} />
      <Typography color={muted ? color : undefined}>{label}</Typography>
    </Box>
  );
}
